<script setup lang="ts">
import { ref, watch, computed, type HTMLAttributes } from 'vue';
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxLabel,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport,
  TagsInputInput,
  TagsInputItem,
  TagsInputItemDelete,
  TagsInputItemText,
  TagsInputRoot,
} from 'radix-vue';
import { Icon } from '@iconify/vue';

type Option = string | { label: string; value: string };

const props = defineProps<{
  modelValue: string[];
  options: Option[];
  placeholder?: string;
  class?: HTMLAttributes['class'];
}>();

const emits = defineEmits<(e: 'update:modelValue', value: string[]) => void>();

const searchTerm = ref('');
const modelValue = computed({
  get: () => props.modelValue,
  set: (val) => emits('update:modelValue', val),
});

watch(modelValue, () => {
  searchTerm.value = '';
});

const resolvedOptions = computed(() =>
  props.options.map((opt) =>
    typeof opt === 'string' ? { label: opt, value: opt } : opt,
  ),
);

const resolvedTags = computed(() => {
  return modelValue.value
    .map((val) => resolvedOptions.value.find((opt) => opt.value === val))
    .filter(Boolean) as { label: string; value: string }[];
});
</script>

<template>
  <ComboboxRoot
    v-model="modelValue"
    v-model:search-term="searchTerm"
    multiple
    :class="['relative mx-auto', props.class]"
  >
    <ComboboxAnchor
      class="inline-flex w-full items-center justify-between gap-1 rounded-md bg-secondary p-2 text-sm shadow outline-none"
    >
      <TagsInputRoot
        v-model="modelValue"
        delimiter=""
        class="flex w-full flex-wrap items-center gap-2 rounded-md bg-secondary"
      >
        <TagsInputItem
          v-for="item in resolvedTags"
          :key="item.value"
          :value="item.value"
          class="flex h-auto items-center gap-1 rounded bg-white/15 p-1 text-sm"
        >
          <span>{{ item.label }}</span>
          <TagsInputItemDelete>
            <Icon icon="lucide:x" class="h-3 w-3" />
          </TagsInputItemDelete>
        </TagsInputItem>

        <ComboboxInput as-child>
          <TagsInputInput
            :placeholder="props.placeholder"
            class="flex-1 rounded bg-transparent px-1 focus:outline-none"
            @keydown.enter.prevent
          />
        </ComboboxInput>
      </TagsInputRoot>

      <ComboboxTrigger
        class="hover:bg-panel rounded-md p-1 transition-transform duration-200 data-[state=open]:rotate-180"
      >
        <IconDropdown class="h-4 w-4 opacity-50" />
      </ComboboxTrigger>
    </ComboboxAnchor>

    <ComboboxContent
      position="popper"
      side="bottom"
      class="z-50 mt-2 w-[var(--radix-combobox-trigger-width)] min-w-[160px] overflow-hidden rounded-md border bg-popover shadow-lg"
    >
      <ComboboxViewport class="p-1">
        <ComboboxEmpty class="py-2 text-center text-xs text-muted-foreground">
          No options
        </ComboboxEmpty>

        <ComboboxGroup>
          <ComboboxItem
            v-for="opt in resolvedOptions"
            :key="opt.value"
            :value="opt.value"
            class="relative flex h-10 select-none items-center rounded-md px-3 text-sm leading-none hover:bg-accent hover:text-accent-foreground"
          >
            <ComboboxItemIndicator
              class="absolute left-0 flex w-6 items-center justify-center"
            >
              <Icon icon="lucide:check" class="h-3 w-3" />
            </ComboboxItemIndicator>
            <span class="relative left-6"> {{ opt.label }} </span>
          </ComboboxItem>
        </ComboboxGroup>
      </ComboboxViewport>
    </ComboboxContent>
  </ComboboxRoot>
</template>
